/**
 * P-04 产品详情（docs/03-UIUX.md §5）。
 *
 * Tab：概要 / 版本 / 套餐 / 签名密钥 / 公钥分发；底部 Danger Zone（归档产品，T2）。
 * 套餐与签名密钥已有独立管理页，此处为只读概览 + 跳转入口，避免功能重复实现。
 */
import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ErrorBlock, EmptyFirstRun, TableSkeleton } from '../../components/DataState';
import { CopyButton } from '../../components/CopyButton';
import { describeError } from '../../api/client';
import {
  deleteProduct,
  getProduct,
  getProductIntegration,
  listPlans,
  listProductVersions,
  listSigningKeys,
  type SigningKey,
} from '../../api/endpoints';
import type { Product, ProductVersion, Plan, ProductIntegration } from '../../api/types';
import { useAsync } from '../../lib/useAsync';
import { formatDate, formatDateTime } from '../../lib/format';
import { Icon } from '../../icons/Icon';
import { ICON_SIZE } from '../../tokens';
import { useAuth } from '../../auth/AuthContext';
import { cssVar } from '../../tokens';

type Tab = 'overview' | 'versions' | 'plans' | 'signing-keys' | 'distribution' | 'integration';

const TABS: { key: Tab; label: string }[] = [
  { key: 'overview', label: '概要' },
  { key: 'versions', label: '版本' },
  { key: 'plans', label: '套餐' },
  { key: 'signing-keys', label: '签名密钥' },
  { key: 'distribution', label: '公钥分发' },
  { key: 'integration', label: 'SDK 接入' },
];

/** base64url → Uint8Array（Ed25519 公钥解码，用于计算指纹） */
function b64uToBytes(b64u: string): Uint8Array<ArrayBuffer> {
  const b64 = b64u.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64.padEnd(Math.ceil(b64.length / 4) * 4, '=');
  const bin = atob(padded);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

export function ProductDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isOwner = currentUser?.role === 'owner';

  const [tab, setTab] = useState<Tab>('overview');
  const [showArchive, setShowArchive] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const product = useAsync(useCallback(() => getProduct(id), [id]), [id]);
  const versions = useAsync(useCallback(() => listProductVersions(id), [id]), [id]);
  const plans = useAsync(useCallback(() => listPlans(id), [id]), [id]);
  const signingKeys = useAsync(useCallback(() => listSigningKeys(id), [id]), [id]);
  const integration = useAsync(useCallback(() => getProductIntegration(id), [id]), [id]);

  const productData: Product | null = product.state.kind === 'ready' ? product.state.data : null;
  const versionList: readonly ProductVersion[] = versions.state.kind === 'ready' ? versions.state.data.items : [];
  const planList: readonly Plan[] = plans.state.kind === 'ready' ? plans.state.data.items : [];
  const keyList: readonly SigningKey[] = signingKeys.state.kind === 'ready' ? signingKeys.state.data : [];

  /** 公钥分发：active 叶密钥的 SPKI 公钥 + 指纹 + 分发 URL */
  const distribution = useMemo(() => {
    const activeLeaf = keyList.find((k) => k.status === 'active' && k.role === 'leaf');
    if (!activeLeaf) return null;
    return { key: activeLeaf, url: `/api/v1/client/products/${id}/keys` };
  }, [keyList, id]);

  const [fingerprint, setFingerprint] = useState<string | null>(null);
  const computeFingerprint = useCallback(async () => {
    if (!distribution) return;
    try {
      const digest = await crypto.subtle.digest('SHA-256', b64uToBytes(distribution.key.public_key_b64u));
      setFingerprint(Array.from(new Uint8Array(digest).slice(0, 8), (b) => b.toString(16).padStart(2, '0')).join(''));
    } catch {
      setFingerprint(null);
    }
  }, [distribution]);

  const handleArchived = () => {
    setShowArchive(false);
    setNotice('产品已归档。');
    navigate('/products');
  };

  return (
    <>
      <div className="page-head">
        <div>
          <p className="text-muted" style={{ marginBottom: 4 }}>
            <Link to="/products" className="link-mono">← 返回产品列表</Link>
          </p>
          <h1 className="page-head__title">{productData ? productData.name : '产品详情'}</h1>
          <p className="page-head__sub">
            {productData ? (
              <>
                <code>{productData.slug}</code> · 撤销列表版本 v{productData.revocation_list_version}
              </>
            ) : '加载中…'}
          </p>
        </div>
      </div>

      {notice && (
        <div className="notice notice--ok" role="status">
          <Icon name="circle-check" size={ICON_SIZE.inline} />
          {notice}
        </div>
      )}

      {product.state.kind === 'failed' ? (
        <div className="table-frame">
          <ErrorBlock message={describeError(product.state.error, '加载产品')} onRetry={product.reload} />
        </div>
      ) : product.state.kind === 'loading' || productData === null ? (
        <div className="card-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
          <div className="skeleton-bar" style={{ height: 160 }} />
          <div className="skeleton-bar" style={{ height: 160 }} />
        </div>
      ) : (
        <>
          <div className="tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`tabs__btn ${tab === t.key ? 'tabs__btn--active' : ''}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'overview' && (
            <div className="card-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
              <div className="panel">
                <div className="panel__body">
                <h3 className="panel__section-title">基本信息</h3>
                <dl className="detail-list">
                    <dt>产品 ID (LH_PRODUCT_ID)</dt>
                    <dd>
                      <code>{productData.id}</code>
                      <CopyButton value={productData.id} what="产品ID" />
                    </dd>
                    <dt>产品标识</dt>
                    <dd><code>{productData.slug}</code></dd>
                    <dt>名称</dt>
                    <dd>{productData.name}</dd>
                    <dt>描述</dt>
                    <dd>{productData.description ?? '-'}</dd>
                    <dt>状态</dt>
                    <dd>
                      <span className={`status-dot status-dot--${productData.is_active ? 'active' : 'archived'}`} />
                      {productData.is_active ? '启用' : '已归档'}
                    </dd>
                  </dl>
                </div>
              </div>
              <div className="panel">
                <div className="panel__body">
                  <h3 className="panel__section-title">安全与时间</h3>
                  <dl className="detail-list">
                    <dt>撤销列表版本</dt>
                    <dd>v{productData.revocation_list_version}</dd>
                    <dt>创建时间</dt>
                    <dd>{formatDateTime(productData.created_at)}</dd>
                    <dt>更新时间</dt>
                    <dd>{productData.updated_at ? formatDateTime(productData.updated_at) : '-'}</dd>
                  </dl>
                  <p className="text-muted" style={{ fontSize: 12 }}>
                    撤销列表版本号由任何撤销/暂停操作递增，客户端据此廉价探测变更。
                  </p>
                </div>
              </div>
            </div>
          )}

          {tab === 'versions' && (
            <>
              {versions.state.kind === 'failed' ? (
                <div className="table-frame">
                  <ErrorBlock message={describeError(versions.state.error, '加载版本')} onRetry={versions.reload} />
                </div>
              ) : versions.state.kind === 'loading' ? (
                <div className="table-frame">
                  <table className="table"><TableSkeleton rows={6} columns={5} /></table>
                </div>
              ) : versionList.length === 0 ? (
                <div className="table-frame">
                  <EmptyFirstRun icon="package" description="暂无版本。版本是纯元数据，不接入授权判定。" />
                </div>
              ) : (
                <div className="table-frame">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>版本号</th>
                        <th>渠道</th>
                        <th>发布说明</th>
                        <th>发布时间</th>
                        <th>创建时间</th>
                      </tr>
                    </thead>
                    <tbody>
                      {versionList.map((v) => (
                        <tr key={v.id}>
                          <td><code>{v.version}</code></td>
                          <td>
                            <span className={`chip ${v.channel === 'stable' ? 'chip--ok' : 'chip--off'}`}>{v.channel}</span>
                          </td>
                          <td className="text-muted" style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {v.release_notes ?? '-'}
                          </td>
                          <td>{v.released_at ? formatDate(v.released_at) : '-'}</td>
                          <td>{formatDate(v.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {tab === 'plans' && (
            <>
              {plans.state.kind === 'failed' ? (
                <div className="table-frame">
                  <ErrorBlock message={describeError(plans.state.error, '加载套餐')} onRetry={plans.reload} />
                </div>
              ) : plans.state.kind === 'loading' ? (
                <div className="table-frame"><p className="text-muted">加载中…</p></div>
              ) : planList.length === 0 ? (
                <div className="table-frame">
                  <EmptyFirstRun icon="layers" description="暂无套餐。" />
                </div>
              ) : (
                <div className="table-frame">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>名称</th>
                        <th>代码</th>
                        <th>设备上限</th>
                        <th>有效期</th>
                        <th>状态</th>
                      </tr>
                    </thead>
                    <tbody>
                      {planList.map((plan) => (
                        <tr key={plan.id}>
                          <td>{plan.name}</td>
                          <td><code>{plan.code}</code></td>
                          <td>{typeof plan.max_devices === 'number' ? plan.max_devices : '—'}</td>
                          <td>{plan.duration_days === null ? '永久' : typeof plan.duration_days === 'number' ? `${plan.duration_days} 天` : '—'}</td>
                          <td>
                            <span className={`status-dot status-dot--${plan.is_active ? 'active' : 'archived'}`} />
                            {plan.is_active ? '启用' : '停用'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p style={{ marginTop: 12 }}>
                <Link to="/plans" className="link-mono">前往套餐与权益管理 →</Link>
              </p>
            </>
          )}

          {tab === 'signing-keys' && (
            <>
              {signingKeys.state.kind === 'failed' ? (
                <div className="table-frame">
                  <ErrorBlock message={describeError(signingKeys.state.error, '加载签名密钥')} onRetry={signingKeys.reload} />
                </div>
              ) : signingKeys.state.kind === 'loading' ? (
                <div className="card-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                  <div className="skeleton-bar" style={{ height: 120 }} />
                  <div className="skeleton-bar" style={{ height: 120 }} />
                </div>
              ) : keyList.length === 0 ? (
                <div className="table-frame">
                  <EmptyFirstRun icon="key-round" description="该产品暂无签名密钥。" />
                </div>
              ) : (
                <div className="card-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                  {keyList.map((key) => (
                    <div key={key.id} className="signing-key-card">
                      <div className="signing-key-card__head">
                        <code className="signing-key-card__kid">{key.key_id}</code>
                        <span className={`status-dot status-dot--${key.status === 'active' ? 'active' : key.status === 'retiring' ? 'hold' : key.status === 'compromised' ? 'danger' : 'archived'}`} />
                        <span className="signing-key-card__status">{key.status}</span>
                      </div>
                      <div className="signing-key-card__meta">
                        <span>{key.role === 'root' ? '根密钥' : '叶密钥'}</span>
                        <span>生效：{formatDate(key.not_before)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p style={{ marginTop: 12 }}>
                <Link to="/signing-keys" className="link-mono">前往签名密钥管理（轮换 / 吊销 / 下线）→</Link>
              </p>
            </>
          )}

          {tab === 'distribution' && (
            <div className="panel" style={{ width: '100%', maxWidth: 720 }}>
              <div className="panel__body">
                <h3 className="panel__section-title">公钥分发</h3>
                {distribution === null ? (
                  <p className="text-muted">该产品暂无 active 叶密钥。首次签发授权时会自动生成。</p>
                ) : (
                  <>
                    <dl className="detail-list">
                      <dt>当前叶密钥</dt>
                      <dd><code>{distribution.key.key_id}</code></dd>
                      <dt>分发端点</dt>
                      <dd>
                        <code className="link-mono">{distribution.url}</code>
                      </dd>
                      <dt>校验指纹（SHA-256 前 8 字节）</dt>
                      <dd>
                        {fingerprint ? (
                          <code>{fingerprint}</code>
                        ) : (
                          <button type="button" className="btn btn--quiet" onClick={computeFingerprint}>
                            计算指纹
                          </button>
                        )}
                      </dd>
                    </dl>
                    <div className="field" style={{ marginTop: 8 }}>
                      <label>公钥全文（SPKI base64url）</label>
                      <p className="plaintext-box">
                        <code className="plaintext-code">{distribution.key.public_key_b64u}</code>
                        <button
                          type="button"
                          className="btn btn--quiet btn--icon"
                          aria-label="复制公钥"
                          title="复制"
                          onClick={() => navigator.clipboard?.writeText(distribution.key.public_key_b64u)}
                        >
                          <Icon name="copy" size={ICON_SIZE.inline} />
                        </button>
                      </p>
                    </div>
                    <p className="text-muted" style={{ fontSize: 12, marginTop: 8 }}>
                      轮换后新密钥在根签名完成前不会出现在分发端点中（requires_root_signature）。客户端按指纹校验公钥集完整性。
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          {tab === 'integration' && (
            <IntegrationTab integration={integration} />
          )}

          <section className="card danger-zone" style={{ marginTop: 24 }}>
            <h2 className="danger-zone__title">危险操作</h2>
            <p className="danger-zone__desc">
              归档产品为软删除：仅置删除标记，存在未撤销授权时后端会拒绝（9005）。
            </p>
            {isOwner ? (
              <button type="button" className="btn btn--danger" onClick={() => setShowArchive(true)}>
                归档产品
              </button>
            ) : (
              <p className="text-muted" style={{ fontSize: 12 }}>归档为 owner 专属操作。</p>
            )}
          </section>
        </>
      )}

      {showArchive && productData !== null && (
        <ArchiveDialog
          product={productData}
          onClose={() => setShowArchive(false)}
          onArchived={handleArchived}
        />
      )}
    </>
  );
}

/** 归档产品（T2）：输入产品 slug 确认，防误触。 */
function ArchiveDialog({
  product,
  onClose,
  onArchived,
}: {
  product: Product;
  onClose: () => void;
  onArchived: () => void;
}) {
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const matched = confirm === product.slug;

  const handle = async () => {
    if (!matched || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await deleteProduct(product.id);
      onArchived();
    } catch (e) {
      setError(e instanceof Error ? e.message : '归档失败');
      setSubmitting(false);
    }
  };

  return (
    <div className="panel-overlay" onClick={onClose}>
      <div className="panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="归档产品">
        <div className="panel__head">
          <h2 className="panel__title">归档产品</h2>
          <button type="button" className="btn btn--quiet" onClick={onClose} aria-label="关闭">
            <Icon name="x" size={ICON_SIZE.control} />
          </button>
        </div>
        <div className="panel__body">
          <p className="text-muted">
            将归档「{product.name}」。归档后产品不再出现在启用列表。存在未撤销授权时后端会拒绝。
          </p>
          <div className="t2-band" style={{ color: cssVar('danger') }}>
            请输入产品标识 <code>{product.slug}</code> 以确认
          </div>
          <div className="field">
            <label>产品标识</label>
            <input type="text" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={product.slug} />
          </div>
          {error && <p className="login-error"><Icon name="octagon-x" size={ICON_SIZE.inline} />{error}</p>}
          <div className="dialog-actions">
            <button type="button" className="btn" onClick={onClose} disabled={submitting}>取消</button>
            <button type="button" className="btn btn--danger" disabled={submitting || !matched} onClick={handle}>
              {submitting ? '归档中…' : '确认归档'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * SDK 接入（P-04 产品详情 tab）：建完产品后一站式给出下游 .env 需要的三个值。
 * LH_PRODUCT_ID / LH_ROOT_PUBLIC_KEY / LH_BASE，全部可单独复制，也能一键复制成 .env 片段。
 */
function IntegrationTab({ integration }: { integration: ReturnType<typeof useAsync<ProductIntegration>> }) {
  const data = integration.state.kind === 'ready' ? integration.state.data : null;
  const envText = data
    ? [
        `# ${data.product_id}`,
        `LH_BASE=${data.base_url}`,
        `LH_PRODUCT_ID=${data.product_id}`,
        data.root_public_key ? `LH_ROOT_PUBLIC_KEY=${data.root_public_key}` : '# LH_ROOT_PUBLIC_KEY= (服务端未配置根密钥：设置 SIGNER_DEV_SEED_root 或 SIGNING_ROOT_PUBLIC_KEY 后可见)',
      ].join('\n')
    : '';

  return (
    <div className="panel" style={{ width: '100%', maxWidth: 820 }}>
      <div className="panel__body">
        <h3 className="panel__section-title">下游 SDK 接入参数</h3>
        {integration.state.kind === 'loading' ? (
          <p className="text-muted">加载中…</p>
        ) : integration.state.kind === 'failed' ? (
          <p className="login-error"><Icon name="octagon-x" size={ICON_SIZE.inline} />{describeError(integration.state.error, '加载接入信息')}</p>
        ) : data === null ? (
          <p className="text-muted">暂无数据。</p>
        ) : (
          <>
            <div className="integration-rows">
              <div className="integration-row">
                <div className="integration-row__meta">
                  <span className="integration-row__key">LH_PRODUCT_ID</span>
                  <span className="integration-row__desc">产品 UUID，对应 license.productId</span>
                </div>
                <code className="integration-row__value">{data.product_id}</code>
                <CopyButton value={data.product_id} what="LH_PRODUCT_ID" />
              </div>
              <div className="integration-row">
                <div className="integration-row__meta">
                  <span className="integration-row__key">LH_ROOT_PUBLIC_KEY</span>
                  <span className="integration-row__desc">根公钥（Ed25519 base64url），客户端 pin 验证公钥集</span>
                </div>
                {data.root_public_key ? (
                  <>
                    <code className="integration-row__value">{data.root_public_key}</code>
                    <CopyButton value={data.root_public_key} what="LH_ROOT_PUBLIC_KEY" />
                  </>
                ) : (
                  <span className="tag tag--warn">服务端未配置根密钥</span>
                )}
              </div>
              <div className="integration-row">
                <div className="integration-row__meta">
                  <span className="integration-row__key">LH_BASE</span>
                  <span className="integration-row__desc">对外可达 API 基址（含 /api/v1）</span>
                </div>
                <code className="integration-row__value">{data.base_url}</code>
                <CopyButton value={data.base_url} what="LH_BASE" />
              </div>
            </div>

            <div className="field" style={{ marginTop: 16 }}>
              <label>.env 片段（含 LH_PRODUCT_ID / LH_ROOT_PUBLIC_KEY / LH_BASE）</label>
              <pre className="plaintext-box"><code className="plaintext-code">{envText}</code></pre>
              <button type="button" className="btn btn--primary" style={{ marginTop: 8 }} onClick={() => navigator.clipboard?.writeText(envText)}>
                <Icon name="copy" size={ICON_SIZE.inline} />
                复制为 .env
              </button>
            </div>
            <p className="text-muted" style={{ fontSize: 12, marginTop: 8 }}>
              将以上值填到下游项目的 .env 即可。若根公钥显示「未配置」，请先在服务端设置 SIGNER_DEV_SEED_root（开发）或 SIGNING_ROOT_PUBLIC_KEY（生产），再回到本页刷新。
            </p>
          </>
        )}
      </div>
    </div>
  );
}
